import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Check, Zap, Crown, Shield, Package, Sparkles, Rocket } from "lucide-react";

const TIERS = [
  {
    key: "express",
    name: "EXPRESS",
    price: "15€",
    days: "5 dias",
    tagline: "O Gatilho da Urgência",
    icon: Zap,
    features: [
      "Selo EXPRESS em toda a plataforma",
      "5 dias no topo dos resultados",
      "Notificação a candidatos com alertas",
      "Ideal para posições urgentes",
    ],
    frame: "bg-gradient-to-br from-orange-50 via-white to-red-50 border-2 border-orange-300 shadow-[0_8px_24px_-8px_rgba(249,115,22,0.25)]",
    ribbon: null,
    icBg: "bg-orange-500/10 ring-2 ring-orange-400/40",
    icColor: "text-orange-500",
    priceColor: "text-orange-600",
    checkColor: "text-orange-500",
    textColor: "text-slate-900",
    subColor: "text-slate-600",
    button: "bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white",
  },
  {
    key: "silver",
    name: "SILVER",
    price: "45€",
    days: "10 dias",
    tagline: "O Equilíbrio Seguro",
    icon: Shield,
    features: [
      "Selo SILVER premium",
      "10 dias no topo dos resultados",
      "E-mails de alerta aos candidatos",
      "Reforço em redes sociais",
      "Mais escolhido pelas empresas",
    ],
    frame: "bg-gradient-to-br from-sky-50 via-white to-cyan-50 border-2 border-sky-400 shadow-[0_12px_32px_-8px_rgba(14,165,233,0.35)] lg:-mt-4 lg:scale-[1.03]",
    ribbon: { text: "Mais popular", cls: "bg-gradient-to-r from-sky-500 to-cyan-500 text-white" },
    icBg: "bg-sky-500/10 ring-2 ring-sky-400/40",
    icColor: "text-sky-600",
    priceColor: "text-sky-700",
    checkColor: "text-sky-500",
    textColor: "text-slate-900",
    subColor: "text-slate-600",
    button: "bg-gradient-to-r from-sky-600 to-cyan-600 hover:from-sky-700 hover:to-cyan-700 text-white",
  },
  {
    key: "gold",
    name: "PREMIUM GOLD",
    price: "99€",
    days: "30 dias",
    tagline: "A Estrela de Ouro",
    icon: Crown,
    features: [
      "Selo GOLD com estrela dourada",
      "30 dias em prioridade máxima",
      "Banner destaque na homepage",
      "E-mails de alerta e newsletter",
      "Publicação em redes sociais premium",
    ],
    frame: "bg-gradient-to-br from-slate-950 via-slate-900 to-amber-950 border-2 border-amber-400 shadow-[0_12px_40px_-8px_rgba(245,158,11,0.55)]",
    ribbon: { text: "★ A Estrela de Ouro", cls: "bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-950" },
    icBg: "bg-amber-400/15 ring-2 ring-amber-400/40",
    icColor: "text-amber-300",
    priceColor: "text-amber-300",
    checkColor: "text-amber-300",
    textColor: "text-white",
    subColor: "text-amber-100/80",
    button: "bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-slate-950 font-black",
  },
];

const PACKS = [
  { name: "PACK START", price: "99€", tagline: "3× SILVER", tone: "slate" },
  { name: "PACK BUSINESS", price: "299€", tagline: "5× SILVER + 2× GOLD", tone: "slate" },
  { name: "PACK CORPORATE", price: "599€", tagline: "10× SILVER + 5× GOLD", tone: "slate" },
];

const AI_PACKS = [
  { name: "AI TRIAGEM 50", price: "90€", tagline: "50 análises de CV com IA", perCv: "1,80€/CV" },
  { name: "AI TRIAGEM 100", price: "150€", tagline: "100 análises de CV com IA", perCv: "1,50€/CV", best: true },
];

export const Pricing = () => {
  const navigate = useNavigate();
  return (
    <section id="precos" className="max-w-7xl mx-auto px-4 sm:px-8 py-20 scroll-mt-20">
      <div className="max-w-2xl mx-auto text-center">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600 mb-3">Preços para empresas</p>
        <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-5xl text-slate-900 leading-none">
          Grátis para publicar.<br />Pagas só visibilidade.
        </h2>
        <p className="text-slate-600 mt-5">
          Três tiers pensados com psicologia de compra — do urgente ao prestígio máximo.
          Sem mensalidades. Para candidatos, sempre gratuito.
        </p>
      </div>

      {/* 3 Tiers com psicologia visual */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-14" data-testid="pricing-plans">
        {TIERS.map((t) => {
          const Ic = t.icon;
          return (
            <div key={t.key}
                 data-testid={`pricing-card-${t.key}`}
                 className={`relative rounded-2xl p-7 sm:p-8 flex flex-col ${t.frame}`}>
              {t.ribbon && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.15em] ${t.ribbon.cls}`}>
                  {t.ribbon.text}
                </div>
              )}
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${t.icBg}`}>
                  <Ic className={`w-5 h-5 ${t.icColor}`} strokeWidth={2.2} />
                </div>
                <div>
                  <p className={`font-heading font-black tracking-tighter text-xl ${t.textColor}`}>{t.name}</p>
                  <p className={`text-[11px] uppercase tracking-[0.15em] font-bold ${t.priceColor}`}>{t.days} em destaque</p>
                </div>
              </div>
              <p className={`text-sm ${t.subColor} italic mb-4`}>« {t.tagline} »</p>
              <div className="flex items-baseline gap-1 mb-6">
                <span className={`font-heading font-black tracking-tighter text-6xl ${t.priceColor}`}>
                  {t.price.replace("€","")}
                </span>
                <span className={`text-2xl font-black ${t.priceColor}`}>€</span>
              </div>
              <ul className="space-y-2.5 mb-8 flex-1">
                {t.features.map((f) => (
                  <li key={f} className={`flex items-start gap-2 text-sm ${t.subColor}`}>
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${t.checkColor}`} strokeWidth={3} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button data-testid={`pricing-cta-${t.key}`}
                      onClick={() => navigate("/auth?tab=register&role=company")}
                      className={`w-full rounded-full py-6 font-bold shadow-lg ${t.button}`}>
                <Sparkles className="w-4 h-4 mr-2" /> Começar com {t.name}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Packs de destaques (créditos) */}
      <div className="mt-16">
        <div className="flex items-center gap-2 mb-4">
          <Package className="w-4 h-4 text-slate-600" />
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600">Packs de créditos — poupa até 40%</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5" data-testid="pricing-packs">
          {PACKS.map((p) => (
            <div key={p.name} className="flex items-center justify-between bg-white rounded-2xl border border-black/5 px-6 py-5 hover:border-slate-300 transition-colors">
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-slate-400" strokeWidth={1.75} />
                <div>
                  <p className="text-sm font-bold text-slate-900">{p.name}</p>
                  <p className="text-xs text-slate-500">{p.tagline}</p>
                </div>
              </div>
              <p className="font-heading font-black tracking-tighter text-2xl text-slate-900">{p.price}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Packs AI Triagem em massa */}
      <div className="mt-14 bg-gradient-to-br from-indigo-950 via-slate-900 to-purple-950 rounded-3xl p-8 sm:p-12 text-white border border-white/10 shadow-2xl">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 mb-4 ring-1 ring-fuchsia-500/40">
              <Sparkles className="w-3.5 h-3.5 text-fuchsia-300" />
              <span className="text-[11px] uppercase tracking-widest font-bold text-fuchsia-200">Motor exclusivo B2B</span>
            </div>
            <h3 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl">
              Triagem de <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 to-amber-300">100 CVs em segundos</span>
            </h3>
            <p className="text-white/80 mt-4 max-w-md">
              A IA analisa dezenas ou centenas de CVs, ordena-os por match score contra a tua vaga
              e destaca os candidatos ideais. Ganhas horas de triagem manual — a vantagem que a concorrência não tem.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-white/70">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-fuchsia-300 shrink-0" strokeWidth={3} /> Upload até 100 CVs de uma só vez</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-fuchsia-300 shrink-0" strokeWidth={3} /> Match score 0-100, forças e lacunas</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-fuchsia-300 shrink-0" strokeWidth={3} /> Tabela ordenável, filtro por experiência</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-fuchsia-300 shrink-0" strokeWidth={3} /> 1 crédito = 1 CV analisado</li>
            </ul>
          </div>
          <div className="grid gap-4">
            {AI_PACKS.map((p) => (
              <div key={p.name}
                   data-testid={`ai-pricing-${p.name.replace(/\s+/g,"-").toLowerCase()}`}
                   className={`relative rounded-2xl p-6 border-2 ${p.best ? "border-amber-400 bg-gradient-to-br from-amber-500/20 via-white/5 to-yellow-500/10" : "border-white/20 bg-white/5"}`}>
                {p.best && (
                  <span className="absolute -top-3 left-6 bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-950 text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-widest">
                    ★ Melhor preço
                  </span>
                )}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      {p.best ? <Rocket className="w-5 h-5 text-amber-300" /> : <Sparkles className="w-5 h-5 text-white/70" />}
                      <p className="font-heading font-black tracking-tighter text-xl">{p.name}</p>
                    </div>
                    <p className="text-sm text-white/60 mt-1">{p.tagline}</p>
                    <p className="text-xs text-fuchsia-300 mt-0.5">{p.perCv}</p>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className={`font-heading font-black tracking-tighter text-5xl ${p.best ? "text-amber-300" : "text-white"}`}>
                      {p.price.replace("€","")}
                    </span>
                    <span className="text-2xl font-black">€</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-10 flex flex-col sm:flex-row items-center gap-3">
        <Button data-testid="pricing-cta" className="rounded-full h-12 px-8 bg-slate-900 hover:bg-slate-800"
          onClick={() => navigate("/auth?tab=register")}>
          Publicar a primeira vaga (grátis)
        </Button>
        <p className="text-xs text-slate-500">Pagamentos seguros por Stripe. Fatura com IVA gerido automaticamente.</p>
      </div>
    </section>
  );
};
